/**
 * The self-serve password change — `PATCH /me/password`.
 *
 * ===========================================================================
 * MOVED OUT OF `SettingsPage.tsx` UNDER DESIGN U8 ITEM 11, AND WHY
 * ===========================================================================
 * `docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3 U8, §5 D16 (owner "go" 2026-09-25): a session
 * minted with the must-change claim is shown ONLY the password screen until it clears.
 * That screen (`auth/PasswordChangeRequiredScreen.tsx`) is rendered by the session
 * provider, and the session provider is what `SettingsPage.tsx` itself imports
 * (`useSession`) — so the form had to live in a file that imports neither, or the module
 * graph would carry a cycle. Nothing about the row's behaviour on the Settings page moved:
 * it is the same component, rendered there without props, and `settings.test.tsx` still
 * drives it through that page.
 *
 * The endpoint EXISTS (`MeController.changePassword`, `{current_password, new_password}` →
 * `{ok, revoked_tokens}`), so this is the live variant; plan §6's inert fallback is not
 * taken. The call is inline rather than in a `features/settings/api.ts`: one request with
 * no shared types and no cache to invalidate.
 *
 * ===========================================================================
 * THE FORCED MODE
 * ===========================================================================
 * `forced` opens the form immediately, removes Cancel (there is nothing to cancel back to),
 * says WHY in the help line, and on success hands the NEW password to `onChanged` instead
 * of showing the "you will be asked to sign in again" outcome — because the caller signs
 * the person straight back in with it. The server has revoked every refresh token by then
 * (`AuthService.changePassword`), so a re-login is the only way forward and the screen does
 * it rather than sending the person to the login form to type the password a third time.
 * The value is passed once, to that one callback, and held in no state afterwards.
 *
 * The help line's "not the one you were given" is a rule the SERVER enforces
 * (`AuthService.changePassword` refuses new === current with 422 `PASSWORD_UNCHANGED`;
 * review finding on U8, 2026-09-25) and `submit()` mirrors beside its length check.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';
import { Alert, Button, TextField } from '../../components/ui';

/** `auth.constants.ts` MIN_PASSWORD_LENGTH — the server's floor, mirrored. */
export const MIN_PASSWORD_LENGTH = 12;

function Help({ children }: { children: ReactNode }): React.JSX.Element {
  return <p className="text-label text-fg-secondary">{children}</p>;
}

type PasswordState =
  | { kind: 'closed' }
  | { kind: 'open' }
  | { kind: 'saving' }
  | { kind: 'done'; revoked: number };

export interface PasswordRowProps {
  /**
   * Design U8 / D16: the session must change its password before anything else. The form
   * opens at once, Cancel is absent, and a successful change is handed to `onChanged`.
   */
  forced?: boolean;
  /** Receives the NEW password once, after the server accepted it. Required with `forced`. */
  onChanged?: (newPassword: string) => Promise<void>;
}

export function PasswordRow({ forced = false, onChanged }: PasswordRowProps = {}): React.JSX.Element {
  const [state, setState] = useState<PasswordState>(forced ? { kind: 'open' } : { kind: 'closed' });
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setState({ kind: 'closed' });
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
  };

  const submit = async (): Promise<void> => {
    // Client checks state the SAME rules the server enforces, so the common
    // refusals are immediate — the server stays the authority on both.
    if (current === '') {
      setError('Type your current password.');
      return;
    }
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(`A new password is at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    // Design U8 / D16: the server refuses new === current (422 `PASSWORD_UNCHANGED`,
    // `AuthService.changePassword`) so an admin-typed value can never become the working
    // password. Mirrored here so the forced screen's "not the one you were given" is a
    // rule the form states before the wire, not after it.
    if (next === current) {
      setError('The new password must differ from the current one.');
      return;
    }
    if (next !== confirm) {
      setError('The two new passwords are not the same.');
      return;
    }
    setError(null);
    setState({ kind: 'saving' });
    try {
      const answer = await apiRequest<{ ok: true; revoked_tokens: number }>('/me/password', {
        method: 'PATCH',
        body: { current_password: current, new_password: next },
      });
      const accepted = next;
      setCurrent('');
      setNext('');
      setConfirm('');
      if (onChanged !== undefined) {
        // The caller re-signs the person in with the accepted value; this form is
        // unmounted by that, so no outcome state is set here.
        await onChanged(accepted);
        return;
      }
      setState({ kind: 'done', revoked: answer.revoked_tokens });
    } catch (cause) {
      setState({ kind: 'open' });
      setError(
        cause instanceof ApiRequestError || cause instanceof ApiTransportError
          ? cause.message
          : 'The password could not be changed. Try again.',
      );
    }
  };

  if (state.kind === 'done') {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="text-label font-medium text-fg">Password</span>
        {/* The success surface is `status/success` tint + text, not an `Alert`:
         * `AlertTone` is danger/warning/info and there is no success member,
         * and adding one would be a change to a shared primitive for one
         * consumer. `role="status"` rather than `role="alert"` — the change
         * succeeded, so it is polite news, not an interruption. */}
        <div
          role="status"
          className="flex w-full flex-col gap-1 rounded-sm bg-ok-bg px-3 py-2.5 text-label text-ok-fg"
        >
          <span className="font-medium">Your password is changed.</span>
          <span>
            {state.revoked === 1
              ? 'One signed-in session was ended.'
              : `${state.revoked} signed-in sessions were ended.`}{' '}
            That includes this browser: you will be asked to sign in again here when this
            session next refreshes.
          </span>
        </div>
      </div>
    );
  }

  const busy = state.kind === 'saving';

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-label font-medium text-fg">Password</span>
      {state.kind === 'closed' ? (
        <>
          <div>
            <Button variant="ghost" onClick={() => setState({ kind: 'open' })}>
              Change password
            </Button>
          </div>
          <Help>
            At least {MIN_PASSWORD_LENGTH} characters. Changing it signs out every browser
            and device, including this one.
          </Help>
        </>
      ) : (
        <form
          className="flex flex-col gap-3.5 rounded-sm border border-line bg-subtle p-3.5"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          <TextField
            label="Repeat the new password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {error === null ? null : (
            <Alert tone="danger" title="The password was not changed.">
              {error}
            </Alert>
          )}
          <Help>
            {forced
              ? `At least ${MIN_PASSWORD_LENGTH} characters, and not the one you were given. Every other browser and device is signed out; this one signs you straight back in.`
              : `At least ${MIN_PASSWORD_LENGTH} characters. Changing it signs out every browser and device, including this one — you will be asked to sign in again here.`}
          </Help>
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={busy}>
              {busy ? 'Changing…' : 'Change password'}
            </Button>
            {forced ? null : (
              <Button variant="quiet" onClick={close} disabled={busy}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
